import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart3,
  Users,
  CalendarDays,
  XCircle,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { useReservations } from '../hooks/useReservations';
import { API_BASE_URL } from '../constants/config';
import { getDateOffsetLocal } from '../utils/formatters';
import StatCard from '../components/statistics/StatCard';
import { PageShell, Card, cn } from '../components/ui';
import { controlClasses } from '../components/ui/Field';
import { useChartTheme } from '../hooks/useChartTheme';
import {
  computeKpis,
  groupByDate,
  groupByBranch,
  groupByStatus,
  groupByWeekday,
  groupByHour,
  groupByPartySize,
  topCustomers,
} from '../utils/reservationStats';

const allRestaurants = [
  'Inova Foz - Vila do Conde',
  'Inova Regio - Vila do Conde',
  'Inova Trindade - Porto',
  'Inova Beach - Azurara',
];

const periodOptions = [
  { value: '30', label: 'Últimos 30 dias' },
  { value: '90', label: 'Últimos 90 dias' },
  { value: '365', label: 'Último ano' },
  { value: 'all', label: 'Tudo' },
];

const statusLabels = {
  pending: 'Pendente',
  confirmed: 'Confirmado',
  completed: 'Concluído',
  canceled: 'Cancelado',
};

const filterControl = cn(controlClasses, 'py-1.5 text-sm');

const ChartCard = ({ title, children }) => (
  <Card className="p-5">
    <h3 className="text-sm font-semibold text-muted mb-4">{title}</h3>
    {children}
  </Card>
);

const Statistics = () => {
  const token = localStorage.getItem('toms_auth_token');
  const onLogout = () => {
    localStorage.removeItem('toms_auth_token');
    window.location.href = '/login';
  };

  const { reservations, loading, error, fetchReservations } = useReservations(
    token,
    API_BASE_URL,
    onLogout
  );

  const theme = useChartTheme();
  const [period, setPeriod] = useState('90');
  const [branch, setBranch] = useState('all');

  // This page's window is its own: it defaults to 90 days and offers longer
  // periods, so it must ask the server for the period being charted. Letting it
  // take the hook's 30-day default would draw a "Último ano" chart from a month
  // of data — wrong numbers, presented as right.
  const wantedWindow = period === 'all' ? null : Number(period);
  const [fetchedWindow, setFetchedWindow] = useState(undefined);

  // Refetches when the period widens. Guarded by comparing windows rather than
  // a `hasFetched` flag because `onLogout` is defined inline above, so
  // `fetchReservations` gets a new identity on every render and this effect
  // re-runs each time.
  useEffect(() => {
    if (!token || fetchedWindow === wantedWindow) return;
    setFetchedWindow(wantedWindow);
    fetchReservations(true, wantedWindow);
  }, [token, wantedWindow, fetchedWindow, fetchReservations]);

  // Apply period + branch filters to the raw reservations.
  const filtered = useMemo(() => {
    const start = period === 'all' ? null : getDateOffsetLocal(Number(period));
    return reservations.filter((r) => {
      if (branch !== 'all' && r.branch !== branch) return false;
      if (start && r.booking_date && r.booking_date < start) return false;
      return true;
    });
  }, [reservations, period, branch]);

  const kpis = useMemo(() => computeKpis(filtered), [filtered]);
  const byDate = useMemo(() => groupByDate(filtered), [filtered]);
  const byBranch = useMemo(() => groupByBranch(filtered), [filtered]);
  const byStatus = useMemo(() => groupByStatus(filtered), [filtered]);
  const byWeekday = useMemo(() => groupByWeekday(filtered), [filtered]);
  const byHour = useMemo(() => groupByHour(filtered), [filtered]);
  const byPartySize = useMemo(() => groupByPartySize(filtered), [filtered]);
  const customers = useMemo(() => topCustomers(filtered, 10), [filtered]);

  const nf = new Intl.NumberFormat('pt-PT');

  // Shared theme-aware recharts props.
  const axisTick = { fontSize: 11, fill: theme.axis };
  const gridProps = { strokeDasharray: '3 3', stroke: theme.grid };
  const tooltipProps = {
    contentStyle: {
      background: theme.surface,
      border: `1px solid ${theme.border}`,
      borderRadius: 12,
      color: theme.foreground,
    },
    labelStyle: { color: theme.foreground },
    itemStyle: { color: theme.foreground },
    cursor: { fill: theme.grid, fillOpacity: 0.3 },
  };
  const legendStyle = { color: theme.foreground, fontSize: 12 };

  const headerExtra = (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-2">
        <label htmlFor="period" className="text-sm font-medium text-muted">
          Período:
        </label>
        <select
          id="period"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className={filterControl}
        >
          {periodOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <label htmlFor="branch" className="text-sm font-medium text-muted">
          Restaurante:
        </label>
        <select
          id="branch"
          value={branch}
          onChange={(e) => setBranch(e.target.value)}
          className={filterControl}
        >
          <option value="all">Todos</option>
          {allRestaurants.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  return (
    <PageShell
      title="Estatísticas de Reservas"
      icon={BarChart3}
      headerExtra={headerExtra}
      actions={
        <button
          // Must pass the current period: defaulting would fetch 30 days and
          // redraw a "Último ano" chart from a month of data.
          onClick={() => fetchReservations(true, wantedWindow)}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-xl bg-surface border border-border px-4 py-2.5 text-sm font-semibold text-foreground shadow-sm hover:bg-surface-2 transition-colors disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <RefreshCw className="h-5 w-5" />}
          {loading ? 'A Carregar...' : 'Atualizar'}
        </button>
      }
    >
      <div className="space-y-6">
        {error && (
          <div className="rounded-xl bg-status-canceled-soft text-status-canceled px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {loading && reservations.length === 0 ? (
          <div className="flex items-center justify-center py-24 text-muted gap-2">
            <Loader2 className="h-6 w-6 animate-spin" />
            A carregar estatísticas...
          </div>
        ) : (
          <>
            {/* KPI row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                label="Total de Reservas"
                value={nf.format(kpis.total)}
                icon={CalendarDays}
                accent="brand"
              />
              <StatCard
                label="Total de Pessoas"
                value={nf.format(kpis.totalGuests)}
                icon={Users}
                accent="blue"
              />
              <StatCard
                label="Tamanho Médio de Grupo"
                value={kpis.avgPartySize.toFixed(1)}
                sublabel="pessoas por reserva"
                icon={Users}
                accent="green"
              />
              <StatCard
                label="Taxa de Cancelamento"
                value={`${kpis.cancellationRate.toFixed(1)}%`}
                sublabel={`${kpis.completionRate.toFixed(1)}% concluídas`}
                icon={XCircle}
                accent="red"
              />
            </div>

            {/* Time trend */}
            <ChartCard title="Reservas e Pessoas ao longo do tempo">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={byDate} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="date" tick={axisTick} minTickGap={24} stroke={theme.grid} />
                  <YAxis allowDecimals={false} tick={axisTick} stroke={theme.grid} />
                  <Tooltip {...tooltipProps} />
                  <Legend wrapperStyle={legendStyle} />
                  <Line
                    type="monotone"
                    dataKey="reservas"
                    name="Reservas"
                    stroke={theme.palette[0]}
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="pessoas"
                    name="Pessoas"
                    stroke={theme.palette[3]}
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Distributions grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ChartCard title="Reservas por Restaurante">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={byBranch} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <CartesianGrid {...gridProps} />
                    <XAxis type="number" allowDecimals={false} tick={axisTick} stroke={theme.grid} />
                    <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 10, fill: theme.axis }} stroke={theme.grid} />
                    <Tooltip {...tooltipProps} />
                    <Bar dataKey="value" name="Reservas" fill={theme.palette[0]} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Reservas por Estado">
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={byStatus}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                    >
                      {byStatus.map((entry) => (
                        <Cell key={entry.name} fill={theme.status[entry.name] || theme.axis} />
                      ))}
                    </Pie>
                    <Tooltip
                      {...tooltipProps}
                      formatter={(value, name) => [value, statusLabels[name] || name]}
                    />
                    <Legend wrapperStyle={legendStyle} formatter={(value) => statusLabels[value] || value} />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Reservas por Dia da Semana">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={byWeekday}>
                    <CartesianGrid {...gridProps} />
                    <XAxis dataKey="name" tick={axisTick} stroke={theme.grid} />
                    <YAxis allowDecimals={false} tick={axisTick} stroke={theme.grid} />
                    <Tooltip {...tooltipProps} />
                    <Bar dataKey="value" name="Reservas" fill={theme.palette[0]} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Horas de Pico">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={byHour}>
                    <CartesianGrid {...gridProps} />
                    <XAxis dataKey="name" tick={axisTick} stroke={theme.grid} />
                    <YAxis allowDecimals={false} tick={axisTick} stroke={theme.grid} />
                    <Tooltip {...tooltipProps} />
                    <Bar dataKey="value" name="Reservas" fill={theme.palette[2]} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Distribuição do Tamanho de Grupo">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={byPartySize}>
                    <CartesianGrid {...gridProps} />
                    <XAxis
                      dataKey="name"
                      tick={axisTick}
                      stroke={theme.grid}
                      label={{ value: 'pessoas', position: 'insideBottom', offset: -2, fontSize: 11, fill: theme.axis }}
                    />
                    <YAxis allowDecimals={false} tick={axisTick} stroke={theme.grid} />
                    <Tooltip {...tooltipProps} />
                    <Bar dataKey="value" name="Reservas" fill={theme.palette[5]} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Top Clientes Recorrentes">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-muted uppercase">
                        <th className="py-2 pr-4">Cliente</th>
                        <th className="py-2 pr-4">Contacto</th>
                        <th className="py-2 pr-4 text-center">Reservas</th>
                        <th className="py-2 text-center">Pessoas</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {customers.length > 0 ? (
                        customers.map((c) => (
                          <tr key={c.key}>
                            <td className="py-2 pr-4 font-medium text-foreground">{c.name}</td>
                            <td className="py-2 pr-4 text-muted">{c.contact}</td>
                            <td className="py-2 pr-4 text-center font-semibold text-foreground">{c.reservas}</td>
                            <td className="py-2 text-center text-foreground">{c.pessoas}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="py-6 text-center text-muted-2">
                            Sem dados no período selecionado.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </ChartCard>
            </div>
          </>
        )}
      </div>
    </PageShell>
  );
};

export default Statistics;
